import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button, Divider } from '@zoom/react-ui';
import { IconCloseSmallV2 } from '@zoom/icons-react';
import LocalTime from './LocalTime';
import {
  GREEN,
  WHITE,
  BLACK,
  determineTextColor,
  determineTimerColor,
  determineBackgroundColor,
} from '../utils';
import useDynamicIndicatorClientCheck from '../hooks/useDynamicIndicatorClientCheck';
import useWithSound from '../hooks/useWithSound';
import { selectedTimerMusicOption } from '../utilities/MusicInTimer/reducer';
import { ControlButton } from './Buttons';

export default function Countdown({
  hours,
  minutes,
  seconds,
  countdownStatus,
  timerTime,
  myDynamicIndicator,
  isTimerDISet,
  resetTimer,
  startTimer,
  userContext,
  isMacOS,
  timerIsRunning,
  setTimerTime,
  startTime,
  supportRunningContext,
  myTimerAudioToggleOn,
  setResumedTime,
  setStartTime,
  setStartTimestamp,
  timerStartLogic,
  isStopwatchDISet,
  timerVideoToggleOn,
  setSomethingWentWrong,
}) {
  const [timerColor, setTimerColor] = useState(GREEN);
  const [timerTextColor, setTimerTextColor] = useState(WHITE);
  const [timerBackgroundColor, setTimerBackgroundColor] = useState(BLACK);
  const selectedMusicOption = useSelector(selectedTimerMusicOption);
  const dynamicIndicatorClientCheck = useDynamicIndicatorClientCheck();
  const clientSupportWithSound = useWithSound();

  const hideControls =
    (isTimerDISet || isStopwatchDISet) && !myDynamicIndicator;

  function calculateOwnerStartTime() {
    const globalStartTime =
      isTimerDISet && !myDynamicIndicator
        ? parseInt(window.localStorage.getItem('PublisherSetTime'))
        : parseInt(window.localStorage.getItem('LastSetTime'));
    return globalStartTime >= 0 ? globalStartTime : startTime;
  }

  async function addThirty() {
    const currentTimerTime = timerTime;
    setTimerTime(currentTimerTime + 30);
    setResumedTime(currentTimerTime + 30);
    setStartTimestamp(Date.now());
    if (isTimerDISet) {
      zoomSdk
        .callZoomApi('extendDynamicIndicator', {
          extendDuration: 30 * 1000,
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  useEffect(() => {
    const ownerStartTime = calculateOwnerStartTime();
    const timerColor = determineTimerColor(timerTime, ownerStartTime);
    const textColor = determineTextColor(timerTime);
    const backgroundColor = determineBackgroundColor(timerTime);
    setTimerColor(timerColor);
    setTimerTextColor(textColor);
    setTimerBackgroundColor(backgroundColor);
  }, [startTime, timerTime]);

  useEffect(() => {
    if (
      isTimerDISet &&
      myDynamicIndicator &&
      dynamicIndicatorClientCheck === 'dISupportedClient'
    ) {
      zoomSdk
        .callZoomApi('setDynamicIndicator', {
          timer: {
            action: 'resume',
            withSound: myTimerAudioToggleOn,
            showNotification: true,
            songChoice: parseInt(selectedMusicOption.id),
            countNegativeAfterAlarm: true,
          },
          borderColor: timerColor,
          backgroundColor: timerBackgroundColor,
          textColor: timerTextColor,
        })
        .catch((error) => {
          setSomethingWentWrong(true);
        });
    }
  }, [
    timerColor,
    timerTextColor,
    timerBackgroundColor,
    myDynamicIndicator,
    clientSupportWithSound,
    myTimerAudioToggleOn,
    selectedMusicOption,
  ]);

  return (
    <>
      <div className='Home__timer_running_container'>
        <div
          className={`Countdown__countdownDisplayTimes`}
          style={{ color: timerColor }}
        >
          {Math.abs(hours) > 0 && (
            <>
              <span className='Home__timer_hour'>{hours}</span>
              <span className='time_colon'>:</span>
            </>
          )}
          <span className='time_min'>
            {timerTime < 0 && Math.abs(hours) > 0
              ? minutes.substring(1)
              : minutes}
          </span>
          <span className='time_colon'>:</span>
          <span className={`time_sec countdown`}>
            {timerTime < 0 ? seconds.substring(1) : seconds}
          </span>
        </div>
        <div className='Countdown__control_container'>
          {!hideControls && timerTime > 0 && (
            <>
              <Button type='tertiary' onClick={() => addThirty()}>
                {' '}
                +30
              </Button>
              <ControlButton
                timerTime={timerTime}
                startTimer={startTimer}
                timerStatus={timerIsRunning}
                countdownStatus={countdownStatus}
                isMacOS={isMacOS}
                timerStartLogic={timerStartLogic}
              />
            </>
          )}
          {(!hideControls || userContext?.role === 'host') && (
            <Button
              variant='icon'
              type='tertiary'
              data-tooltip-id='timer-cancelbutton-tooltip'
              data-tooltip-delay-show={300}
              onClick={resetTimer}
            >
              <IconCloseSmallV2 />
            </Button>
          )}
        </div>
      </div>
      <Divider />
    </>
  );
}
